import BookingModal from "@/components/BookingModal";
import { Button } from "@/components/ui/button";
import WhatsAppButton from "@/components/v2/molecules/landing/layout/WhatsAppButton";
import { useDeviceDetection } from "@/hooks/useDeviceDetection";
import { useResponsiveComponent } from "@/hooks/useResponsiveComponent";
import { useAmplitudeEvents } from "@/utils/amplitudeEvents";
import { CLEANING_FEE, MOBILE_BOOKING_BAR_HEIGHT } from "@/utils/constants";
import {
  getPropertyDisplayPrice,
  getPropertyShortName,
} from "@/utils/propertyUtils";
import { useMemo, useState } from "react";

interface MobileBookingBarProps {
  price: number;
  checkIn?: Date;
  checkOut?: Date;
  onDateChange: (checkIn: Date | undefined, checkOut: Date | undefined) => void;
  unavailableDates?: Date[];
  maxGuests?: number;
  airbnbUrl?: string;
  propertyTitle: string;
  icalUrl?: string;
  property?: {
    rating: number;
    guests: number;
    reviews?: unknown[];
    images?: string[];
    host?: { name: string };
  };
}

const MobileBookingBar = ({
  price,
  checkIn,
  checkOut,
  onDateChange,
  propertyTitle,
  property,
  maxGuests = 6,
}: MobileBookingBarProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showMobileVersion } = useResponsiveComponent();
  const { isIOS } = useDeviceDetection();
  const { trackRequestReservation } = useAmplitudeEvents();

  const calculateNights = (checkInDate: Date, checkOutDate: Date) => {
    const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  };

  const bookingData = useMemo(() => {
    const hasDatesSelected = checkIn && checkOut;
    const nights = hasDatesSelected ? calculateNights(checkIn, checkOut) : 0;
    const subtotal = nights > 0 ? price * nights : 0;
    const totalPrice = subtotal + CLEANING_FEE;

    return {
      hasDatesSelected,
      nights,
      totalPrice,
    };
  }, [checkIn, checkOut, price]);

  const handleButtonClick = () => {
    if (!bookingData.hasDatesSelected) {
      const calendarTitle = document.querySelector("[data-calendar-title]");

      if (calendarTitle) {
        calendarTitle.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    } else {
      const propertyShortName = getPropertyShortName(propertyTitle);
      const totalReviews = property?.reviews ? property.reviews.length : 0;
      const totalPhotos = property?.images ? property.images.length : 0;
      const hostName = property?.host?.name || "";

      trackRequestReservation(
        checkIn,
        checkOut,
        "1", // Default guests for mobile
        propertyTitle,
        propertyShortName,
        property?.rating || 0,
        property?.guests || maxGuests,
        price,
        totalReviews,
        totalPhotos,
        hostName,
      );

      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onDateChange?.(undefined, undefined);
  };

  const getButtonText = () => {
    return bookingData.hasDatesSelected
      ? "Solicitar reservación"
      : "Seleccionar fechas";
  };

  if (!showMobileVersion) return null;

  const dynamicStyles = isIOS
    ? {
        position: "fixed" as const,
        bottom: 0,
        left: 0,
        right: 0,
        height: "auto",
        minHeight: `${MOBILE_BOOKING_BAR_HEIGHT}px`,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        transform: "translateZ(0)",
        willChange: "transform",
      }
    : {};

  return (
    <>
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50"
        style={dynamicStyles}
      >
        <div className="flex items-center justify-between">
          <div>
            {bookingData.hasDatesSelected ? (
              <>
                <div className="flex items-baseline space-x-1">
                  <span className="text-lg font-semibold text-gray-900">
                    {getPropertyDisplayPrice(bookingData.totalPrice)}
                  </span>
                  <span className="text-sm text-gray-600">total</span>
                </div>
                <div className="text-xs text-gray-500">
                  {bookingData.nights}{" "}
                  {bookingData.nights === 1 ? "noche" : "noches"} + limpieza
                </div>
              </>
            ) : (
              <>
                <div className="flex items-baseline space-x-1">
                  <span className="text-lg font-semibold text-gray-900">
                    {getPropertyDisplayPrice(price)}
                  </span>
                  <span className="text-sm text-gray-600">/noche</span>
                </div>
                <div className="text-xs text-gray-500">
                  {getPropertyDisplayPrice(CLEANING_FEE)} limpieza
                </div>
              </>
            )}
          </div>

          <Button
            onClick={handleButtonClick}
            className="bg-brand-primary hover:bg-brand-primary-hover text-white px-6 py-2 rounded-lg"
          >
            {getButtonText()}
          </Button>
        </div>
      </div>

      <style>{`
        body {
          padding-bottom: ${
            isIOS
              ? `calc(${MOBILE_BOOKING_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`
              : `${MOBILE_BOOKING_BAR_HEIGHT}px`
          } !important;
        }
        
        @supports (height: 100dvh) {
          .mobile-booking-bar {
            height: 100dvh;
          }
        }
        
        @media screen and (max-width: 768px) and (-webkit-min-device-pixel-ratio: 1) {
          .mobile-booking-bar {
            position: -webkit-sticky;
            position: sticky;
            bottom: env(safe-area-inset-bottom, 0);
          }
        }
      `}</style>

      <BookingModal
        isOpen={isModalOpen}
        onSubmit={handleCloseModal}
        closeModal={ () => setIsModalOpen(false) }
        propertyTitle={propertyTitle}
        checkIn={checkIn}
        checkOut={checkOut}
        nights={bookingData.nights}
        totalPrice={bookingData.totalPrice}
        property={property}
        maxGuests={maxGuests}
        price={price}
      />

      <WhatsAppButton isVisible={!isModalOpen} />
    </>
  );
};

export default MobileBookingBar;
